// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import apiClient from "../api/apiClient";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper to sync user + storage
  const setUserAndStorage = (userData) => {
    if (userData) {
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
    } else {
      setUser(null);
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    }
  };

  // Add a helper to update AI usage in user context
  const updateAiUsage = (used, limit) => {
    setUser((prev) => ({
      ...prev,
      aiQueriesUsed: used,
      limits: { ...prev.limits, aiQueries: limit },
    }));
  };

  console.log("AuthContext user:", user);

  // Fetch fresh user (auth + watchlist)
  const fetchUser = async () => {
    try {
      const { data: userData } = await apiClient.get("/auth/me");
      const { data: watchlistData } = await apiClient.get("/watchlist");

      setUserAndStorage({
        ...userData,
        watchlist: watchlistData || [],
      });
    } catch (err) {
      console.error("Failed to fetch user:", err);
      setUserAndStorage(null);
    } finally {
      setLoading(false);
    }
  };

  // Login
  const login = async (email, password) => {
    try {
      const { data } = await apiClient.post("/auth/login", { email, password });
      if (data.token) {
        localStorage.setItem("token", data.token);
        await fetchUser();
      }
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || "Login failed";
      throw new Error(message);
    }
  };

  // Signup
  const signup = async (email, password) => {
    try {
      const { data } = await apiClient.post("/auth/signup", {
        email,
        password,
      });
      if (data.token) {
        localStorage.setItem("token", data.token);
        await fetchUser();
      }
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || "Signup failed";
      throw new Error(message);
    }
  };

  // Logout
  const logout = () => {
    setUserAndStorage(null);
  };

  // Ask AI (updates usage automatically)
  const askAI = async (ticker) => {
    try {
      const { data } = await apiClient.post("/ask", { ticker });
      const { answer } = data; // removed unused "usage"
      return answer;
    } catch (err) {
      console.error("askAI error:", err.response?.data || err.message);
      throw err;
    }
  };

  // Add ticker to watchlist
  const addTickerToWatchlist = async (ticker) => {
    try {
      const res = await apiClient.post("/watchlist", { watchlist: [ticker] });
      setUser((prev) => ({
        ...prev,
        watchlist: res.data.watchlist,
        watchlistCount: res.data.watchlist.length,
      }));
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || "Failed to add ticker";

      console.error("Error adding ticker:", message);

      if (message.includes("Cannot exceed your watchlist limit")) {
        throw new Error(message);
      }

      throw err;
    }
  };

  // Remove ticker from watchlist
  const removeTickerFromWatchlist = async (ticker) => {
    try {
      const res = await apiClient.delete(`/watchlist/${ticker}`);
      setUser((prev) => ({
        ...prev,
        watchlist: res.data.watchlist,
      }));
    } catch (err) {
      console.error(
        "Error removing ticker:",
        err.response?.data || err.message
      );
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) fetchUser();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signup,
        logout,
        setUser,
        fetchUser,
        askAI,
        updateAiUsage,
        addTickerToWatchlist,
        removeTickerFromWatchlist,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
